import { createContext, useContext } from 'react';

/**
 * Promise-based replacement for window.confirm().
 *
 * Deliberately mirrors toastContext: App owns the state and renders the dialog,
 * and publishes its `confirm` function here for everything below it. The state
 * cannot live in a provider component that App renders, because React context
 * flows to DESCENDANTS only — App itself would read the default below and every
 * guard would silently resolve false, turning "delete this?" checks into dead
 * no-ops. App therefore calls its own in-scope `confirm`, and children use this
 * hook.
 *
 * Usage in a child component:
 *
 *   const confirm = useConfirm();
 *   ...
 *   if (!await confirm('Delete this event?')) return;
 *
 * or with options:
 *
 *   if (!await confirm({
 *     message: 'This permanently removes the correction.',
 *     title: 'Delete correction?',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   })) return;
 *
 * The enclosing handler must become `async`. That is the only behavioural
 * difference: window.confirm paused synchronously, this awaits.
 *
 * The default resolves to FALSE, so a component rendered outside the provider
 * declines rather than performing a destructive action unprompted.
 */
const ConfirmContext = createContext(async () => false);

export const ConfirmProvider = ConfirmContext.Provider;

export function useConfirm() {
  return useContext(ConfirmContext);
}

export default ConfirmContext;
